// Ported from design/project/App10-CallSide.dc.html, then wired: picking a side joins the queue.
import { useEffect, useState } from "react";
import { play } from "../sound.js";
import { fmtPrice, lastPrice } from "../format.js";
import { payoutFor, useGame, type Side } from "../game.js";

const ERRORS: Record<string, string> = {
  low_balance: "Not enough balance for this stake. Top up first.",
  already_in_battle: "You are already in a battle.",
  already_queued: "You are already searching for an opponent.",
  arena_closed: "This arena is closed right now.",
  stake_not_allowed: "That stake is above your limit.",
  network: "No connection. Check your data and try again.",
};
const FALLBACK = "Something went wrong. Try again.";
/** The arrows give way on short phones, so the OR badge in the middle never sits on a label. */
const ARROW = "clamp(52px, calc(27.5dvh - 116px), 104px)";

export function App10_CallSide() {
  const { selection, prices, findOpponent, error, topUp, clearError, feeNow } = useGame();
  // A message belongs to this visit: it goes when the player leaves, so it never greets them next time.
  useEffect(() => () => clearError(), []);
  const [busy, setBusy] = useState(false);
  const pick = async (side: Side) => {
    if (busy) return;
    play("tap");
    setBusy(true);
    try {
      await findOpponent(side);
    } catch {
      setBusy(false);
    }
  };
  const arena = selection.arena;
  // Joining the queue is the commitment: the stake locks at once and a match starts the battle.
  const { payout } = payoutFor(selection.stake, feeNow());
  return (
    <>
      <div className="screen">
  <div className="pad" style={{ height: "100%", padding: "var(--dd-safe-top) 0 0" }}>
    <div className="row tgbar">
      
      <div className="panel" style={{ height: "38px", padding: "0 12px", display: "flex", alignItems: "center", gap: "8px", borderRadius: "12px" }}>
        <span className="unb" style={{ fontWeight: "900", fontSize: "11px" }}>{arena === "XAU" ? "GOLD" : arena}</span>
        <span className="mono" style={{ fontWeight: "800", fontSize: "13px" }}>{fmtPrice(arena, lastPrice(prices[arena]))}</span>
        <span style={{ width: "1px", height: "16px", background: "#30363D" }}></span>
        <span className="mono lime" style={{ fontWeight: "800", fontSize: "13px" }}>${selection.stake}</span>
      </div>
    </div>

    <div style={{ padding: "0 20px", marginTop: "0", textAlign: "center" }}>
      <span className="unb" style={{ fontWeight: "900", fontSize: "22px" }}>CALL YOUR SIDE</span>
      <p className="muted" style={{ margin: "6px 0 0", fontSize: "13px" }}>Where is {arena === "XAU" ? "gold" : arena} in {selection.durationSec === 60 ? "1 minute" : "3 minutes"}?</p>
    </div>

    <div style={{ marginTop: "18px", flex: "1", position: "relative", display: "flex", flexDirection: "column" }}>
      <button disabled={busy} onClick={() => void pick("long")} aria-label="Go long: price goes up" className="htL" style={{ flex: "1", border: "0", clipPath: "polygon(0 0, 100% 0, 100% 86%, 0 100%)", color: "#FFFFFF", position: "relative", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px", paddingBottom: "40px" }}>
        <span aria-hidden="true" style={{ position: "absolute", inset: "0", background: "radial-gradient(circle at 50% 45%, rgba(204,255,0,0.3), rgba(204,255,0,0) 60%)" }}></span>
        <svg width="92" height="104" viewBox="0 0 120 150" style={{ position: "relative", height: ARROW, width: "auto" }} aria-hidden="true"><path d="M60 4 L116 70 H82 V146 H38 V70 H4 Z" fill="#CCFF00" stroke="#0B0E14" strokeWidth="6" strokeLinejoin="round"></path></svg>
        <span className="big" style={{ position: "relative", fontSize: "44px", color: "#CCFF00" }}>LONG</span>
        <span className="unb" style={{ position: "relative", fontWeight: "700", fontSize: "11px", letterSpacing: "0.1em" }}>PRICE GOES UP</span>
      </button>
      <button disabled={busy} onClick={() => void pick("short")} aria-label="Go short: price goes down" className="htR" style={{ flex: "1", marginTop: "-60px", border: "0", clipPath: "polygon(0 14%, 100% 0, 100% 100%, 0 100%)", color: "#FFFFFF", position: "relative", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px", paddingTop: "40px" }}>
        <span aria-hidden="true" style={{ position: "absolute", inset: "0", background: "radial-gradient(circle at 50% 55%, rgba(255,51,102,0.3), rgba(255,51,102,0) 60%)" }}></span>
        <span className="unb" style={{ position: "relative", fontWeight: "700", fontSize: "11px", letterSpacing: "0.1em" }}>PRICE GOES DOWN</span>
        <span className="big" style={{ position: "relative", fontSize: "44px", color: "#FF3366", textShadow: "4px 4px 0 #0B0E14, 8px 8px 0 #CCFF00" }}>SHORT</span>
        <svg width="92" height="104" viewBox="0 0 120 150" style={{ position: "relative", height: ARROW, width: "auto" }} aria-hidden="true"><path d="M60 146 L116 80 H82 V4 H38 V80 H4 Z" fill="#FF3366" stroke="#0B0E14" strokeWidth="6" strokeLinejoin="round"></path></svg>
      </button>
      <div aria-hidden="true" style={{ position: "absolute", left: "50%", top: "50%", width: "64px", height: "64px", margin: "-32px 0 0 -32px", borderRadius: "50%", background: "#0B0E14", border: "3px solid #FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Unbounded, sans-serif", fontWeight: "900", fontSize: "15px", boxShadow: "0 0 0 4px #0B0E14" }}>OR</div>
      <span style={{ position: "absolute", left: "50%", bottom: "44px", transform: "translateX(-50%)", whiteSpace: "nowrap", padding: "6px 12px", borderRadius: "999px", background: "rgba(11,14,20,0.88)", border: "1px solid rgba(255,255,255,0.1)", fontSize: "11px", color: "#8B949E" }}>
        {error === "low_balance" ? (
          // Can't cover the stake: one tap tops up (play money now, the wallet once real money is on).
          <button onClick={() => void topUp()} style={{ font: "inherit", border: "0", background: "transparent", color: "#FF5C85", cursor: "pointer", padding: "0" }}>
            Not enough balance · <span style={{ color: "#CCFF00", fontWeight: "700" }}>TOP UP</span>
          </button>
        ) : error ? (
          <span className="red">{ERRORS[error] ?? FALLBACK}</span>
        ) : (
          <>${Number(selection.stake)} locks when you join · <span className="lime mono">Win +{payout}</span> · <span className="red mono">Lose -{selection.stake}</span></>
        )}
      </span>
    </div>
  </div>
</div>
    </>
  );
}
